import { createContext, useContext } from "react";

/**
 * Context type for the notification system.
 * @property permission - Current notification permission state
 * @property requestPermission - Function to request notification permission
 * @property disableNotifications - Function to unsubscribe from push notifications
 * @property isSupported - Whether the browser supports notifications
 * @property isSubscribed - Whether the user is currently subscribed to push notifications
 */
export type NotificationContextType = {
  permission: NotificationPermission;
  requestPermission: () => Promise<boolean>;
  disableNotifications: () => Promise<boolean>;
  isSupported: boolean;
  isSubscribed: boolean;
};

export const NotificationContext = createContext<NotificationContextType | null>(null);

/**
 * Hook to access notification context.
 * Throws an error if used outside of NotificationProvider.
 */
export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};
